import type { VoidFunctionComponent } from 'react';
import { useMemo, useState } from 'react';
import Header from '../../../shared/settings/SettingsPageHeader';
import Section from '../../../shared/settings/SettingsPageSection';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import { window } from '../../../../globals';
import type { ProjectUpdateHooksSettings } from '../../../../../shared/settings/project/update-hooks';
import ProjectUpdateHookSettings from '../../../shared/settings/ProjectUpdateHookSettings';
import Loader from '../../../generic/Loader';
import PropagateProjectUpdateHooksModal from './PropagateUpdateHooksModal';
import { shouldDisplayProjectUpdateHookSettings } from '../../shared/permissions';
import OrganizationSettingsPermissionGate from '../../OrganizationSettingsPermissionGate';
import type { UpdateHookScheduledInterval } from '../../../../../shared/model-typings';

/**
 * SettingsOrganizationProjectUpdateHooks is the settings page for managing
 * the default project scan frequency settings.
 */
const SettingsOrganizationProjectUpdateHooks: VoidFunctionComponent = () => {
  //  Note that we memoize this so that our useOrganizationSettings hook doesn't re-fetch (unless orgId actually changes)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- This is intentional, despite the fact it would never happen.
  const endpoint = useMemo<OrganizationSettingsEndpoint>(() => `/api/organizations/${window.user?.organizationId ?? 0}/settings/projects/update-hooks`, [window.user?.organizationId]);
  const {
    settings,
    isDirty,
    isLoading,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
  } = useOrganizationSettings<ProjectUpdateHooksSettings>({ endpoint, settingsSection: 'project update hooks' });
  const [shouldDisplayPropagateModal, setShouldDisplayPropagateModal] = useState<boolean>(false);

  if (isLoading.fetch) {
    return <Loader />;
  }

  return (
    <>
      <Header heading="Update Hooks Settings">
        <Header.SaveButton
          onClick={onSaveSettings}
          isLoading={isLoading.save}
          isDisabled={isSaveButtonDisabled}
        />
      </Header>
      <Section>
        <Section.Left heading="Default Scan Frequency">
          <Section.Paragraph>
            Enabling this will turn on update hooks for all new projects.
          </Section.Paragraph>
          <Section.PropagateButton
            isDisabled={isDirty}
            onClick={() => setShouldDisplayPropagateModal(true)}
          />
        </Section.Left>
        <Section.Right>
          <ProjectUpdateHookSettings
            isLoading={isLoading.any}
            isUpdateHooksEnabled={settings?.updateHookDefaultScheduledEnabled || false}
            onToggleIsUpdateHooksEnabled={isEnabled =>
              onUpdateSettings({ updateHookDefaultScheduledEnabled: isEnabled })}
            scheduledInterval={settings?.updateHookDefaultScheduledInterval as UpdateHookScheduledInterval | null}
            onScheduledIntervalChange={scheduledInterval =>
              onUpdateSettings({ updateHookDefaultScheduledInterval: scheduledInterval })}
            scheduledIntervalLength={settings?.updateHookDefaultScheduledIntervalLength as number}
            onScheduledIntervalLengthChange={scheduledIntervalLength =>
              onUpdateSettings({ updateHookDefaultScheduledIntervalLength: scheduledIntervalLength })}
            scheduledIntervalTime={settings?.updateHookDefaultScheduledIntervalTime as string}
            onScheduledIntervalTimeChange={scheduledIntervalTime =>
              onUpdateSettings({ updateHookDefaultScheduledIntervalTime: scheduledIntervalTime })}
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateProjectUpdateHooksModal
          isOpen={shouldDisplayPropagateModal}
          settings={settings}
          onHide={() => setShouldDisplayPropagateModal(false)}
        />
      )}
    </>
  );
};

/**
 * SettingsOrganizationProjectUpdateHooksContainer displays the organization project update hooks settings page if the user has permission to view it.
 */
const SettingsOrganizationProjectUpdateHooksContainer: VoidFunctionComponent = () => (
  <OrganizationSettingsPermissionGate checkPermission={shouldDisplayProjectUpdateHookSettings}>
    <SettingsOrganizationProjectUpdateHooks />
  </OrganizationSettingsPermissionGate>
);

export default SettingsOrganizationProjectUpdateHooksContainer;
